import { cva, type VariantProps } from 'class-variance-authority';

/** Width choices shared by the button variant map and Storybook controls. */
export const BUTTON_WIDTHS = ['auto', 'full'] as const;

/** Resting underline choices shared by the button variant map and Storybook controls. */
export const BUTTON_UNDERLINES = ['hover', 'always'] as const;

/** Hover treatment choices shared by the button variant map and Storybook controls. */
export const BUTTON_HOVER_TREATMENTS = ['solid', 'gradient'] as const;

/** Author-facing sizes shown in the Storybook Size control; `flush` stays code-only. */
export const BUTTON_SIZES = ['xxs', 'xs', 'sm', 'lg', 'icon'] as const;

type ButtonWidth = (typeof BUTTON_WIDTHS)[number];
type ButtonUnderline = (typeof BUTTON_UNDERLINES)[number];
type ButtonHoverTreatment = (typeof BUTTON_HOVER_TREATMENTS)[number];

const buttonWidthClass = {
  auto: 'w-auto',
  full: 'w-full',
} as const satisfies Record<ButtonWidth, string>;

const buttonWidthMdClass = {
  auto: 'md:w-auto',
  full: 'md:w-full',
} as const satisfies Record<ButtonWidth, string>;

const buttonWidthLgClass = {
  auto: 'lg:w-auto',
  full: 'lg:w-full',
} as const satisfies Record<ButtonWidth, string>;

const buttonUnderlineClass = {
  hover: '',
  always: '',
} as const satisfies Record<ButtonUnderline, string>;

const buttonHoverTreatmentClass = {
  solid: '',
  gradient: '',
} as const satisfies Record<ButtonHoverTreatment, string>;

/**
 * Design role names, each mapped to the legacy variant it renders identically
 * to. The legacy names stay as deprecated aliases of the role names.
 */
export const BUTTON_ROLE_VARIANTS = {
  primary: 'primaryFill',
  secondary: 'blackFill',
  outlined: 'blackOutline',
  text: 'textOnlyNeutral',
} as const;

/** Variants whose height follows the per-size height tokens. */
const SIZED_VARIANTS = [
  'primary',
  'primaryFill',
  'primaryOutline',
  'secondary',
  'blackFill',
  'outlined',
  'blackOutline',
  'grayOutline',
  'errorOutline',
] as const;

/** Text-only variants: no padding, auto box, text-only height and radius tokens. */
export const TEXT_ONLY_VARIANTS = [
  'textOnly',
  'text',
  'textOnlyNeutral',
  'textOnlySecondary',
  'textOnlyWhite',
  'textOnlyLink',
] as const;

/**
 * Height a `<button>` takes in place of the shadcn base `h-10`: the text-only
 * token, or the `sm` / `lg` height token on sized variants. Both fall back to
 * that same 2.5rem. Anchors have no base height and size to their content,
 * with the height token as a minimum.
 */
export const nativeButtonHeightClass = (
  variant: ButtonVariantProps['variant'],
  size: ButtonVariantProps['size'],
): string | undefined => {
  const resolvedVariant =
    variant === undefined ? buttonDefaultVariants.variant : variant;
  const resolvedSize = size === undefined ? buttonDefaultVariants.size : size;
  if ((TEXT_ONLY_VARIANTS as readonly unknown[]).includes(resolvedVariant)) {
    return 'h-button-text-only';
  }
  if (!(SIZED_VARIANTS as readonly unknown[]).includes(resolvedVariant)) {
    return undefined;
  }
  if (resolvedSize === 'sm') return 'h-button-sm';
  if (resolvedSize === 'lg') return 'h-button-lg';
  return undefined;
};

/** Text-only variants that can stay underlined at rest. */
const UNDERLINE_AT_REST_VARIANTS = [
  'textOnly',
  'text',
  'textOnlyNeutral',
  'textOnlySecondary',
  'textOnlyWhite',
] as const;

/**
 * The hover gradient paints on a `::before` layer under the label so it can
 * fade with opacity, which `background-image` cannot. The layer keeps its own
 * transition so it fades whatever `--cds-button-transition-property` lists.
 * `hover:` still matches while pressing, so pressed states hide it again.
 * Under reduced motion the wash appears without fading.
 */
const GRADIENT_LAYER = [
  'relative isolate',
  "before:content-[''] before:absolute before:inset-0 before:z-[-1]",
  'before:rounded-[inherit] before:pointer-events-none before:opacity-0',
  'before:transition-opacity before:duration-button-gradient before:ease-button',
  'motion-reduce:before:transition-none',
  'hover:before:opacity-100 active:before:opacity-0 aria-pressed:before:opacity-0 disabled:before:opacity-0',
];

/** Text-only buttons never shrink when pressed. */
const NO_PRESS_SCALE = 'active:[scale:none] [&:active>*]:[scale:none]';

/** Icons in the two smallest sizes follow the label size unless the icon token is set. */
const LABEL_SIZED_ICON =
  '[&_svg]:size-button-icon-label [&_[data-hen-icon]]:size-button-icon-label';

/**
 * Line weight of the icons Button draws for `iconBefore`, `iconAfter` and
 * `expandedIconAfter`: `--cds-button-icon-stroke-width`, 1.5 when unset.
 * Icons passed as children keep their own weight, and pack icons
 * (`[data-hen-icon]`) have their stroke drawn into the asset.
 */
export const BUTTON_ICON_STROKE_CLASS = 'stroke-button-icon';

/** Icon sizes an author can set on Button's own icons: the `Icon` author sizes without `full`. */
export const BUTTON_ICON_SIZES = ['sm', 'md', 'lg', 'xl'] as const;

/** Icon colors an author can set on Button's own icons: the `Icon` author colors. */
export const BUTTON_ICON_COLORS = [
  'default',
  'white',
  'primary',
  'secondary',
  'black',
  'neutral',
] as const;

export type ButtonIconSize = (typeof BUTTON_ICON_SIZES)[number];
export type ButtonIconColor = (typeof BUTTON_ICON_COLORS)[number];

/** The chip's saved fill reads this; Button points it at the icon color when one is set. */
export const BUTTON_PRESSED_FILL_VAR = '--cds-button-color-fill-chip-pressed';

/**
 * Same sizes as the `Icon` size variant, marked important so an explicit
 * `iconSize` beats the per-size `[&_svg]` / `[&_[data-hen-icon]]` rules.
 */
export const BUTTON_ICON_SIZE_CLASS = {
  sm: '!w-4 !h-4',
  md: '!w-6 !h-6',
  lg: '!w-8 !h-8',
  xl: '!w-10 !h-10',
} as const satisfies Record<ButtonIconSize, string>;

const PRIMARY_STYLE = [
  'bg-button-primary text-white',
  'hover:bg-button-primary-hover',
  'active:bg-button-primary-active',
  'aria-pressed:bg-button-primary-active',
  'focus-visible:outline-button-focus-primary',
  'disabled:bg-button-primary-disabled disabled:text-button-white-disabled',
  'disabled:hover:bg-button-primary-disabled-hover',
];

const SECONDARY_STYLE = [
  'bg-button-secondary text-white',
  'hover:bg-button-secondary-hover',
  'active:bg-button-secondary-active',
  'aria-pressed:bg-button-secondary-active',
  'disabled:bg-button-secondary-disabled disabled:text-button-white-disabled',
  'disabled:hover:bg-button-secondary-disabled-hover',
];

const OUTLINED_STYLE = [
  'bg-button text-button-outlined',
  'border-button border-button-outlined',
  'hover:bg-button-hover',
  'active:bg-button-active',
  'aria-pressed:bg-button-active',
  'disabled:bg-button-disabled disabled:text-button-outlined-disabled disabled:border-button-outlined-disabled',
  'disabled:hover:bg-button-disabled-hover',
];

const TEXT_STYLE = [
  'bg-transparent text-body rounded-button-text-only',
  'underline-offset-button decoration-button-link',
  'hover:bg-transparent hover:underline hover:text-button-hover',
  'hover:decoration-button-hover-link hover:decoration-button-underline-link',
  'active:decoration-button-hover-link active:decoration-button-underline-link-active',
  'active:bg-transparent active:text-button-active',
  NO_PRESS_SCALE,
  'disabled:text-button-body-disabled disabled:hover:text-button-disabled-hover',
  '!p-0',
];

/** Padded like a button with no fill or border until hovered. */
const GHOST_STYLE = [
  'bg-transparent text-button-ghost',
  'underline-offset-button decoration-button',
  'hover:bg-button-ghost-hover hover:underline hover:decoration-button-underline-hover',
  'active:bg-button-ghost-hover',
];

const PRIMARY_VARIANTS = ['primary', 'primaryFill'] as const;
const SECONDARY_VARIANTS = ['secondary', 'blackFill'] as const;

export const buttonDefaultVariants = {
  variant: 'primary',
  size: 'sm',
  width: 'auto',
  underline: 'hover',
  hoverTreatment: 'solid',
  inverse: false,
} as const satisfies {
  variant: 'primary';
  size: 'sm';
  width: ButtonWidth;
  underline: ButtonUnderline;
  hoverTreatment: ButtonHoverTreatment;
  inverse: boolean;
};

/**
 * HEN Button variants using HEN Design System tokens.
 * All styling references design tokens defined in tailwind.config.ts.
 *
 * Every tenant token falls back to today's rendering when unset, except the
 * icon stroke, which falls back to the brand weight of 1.5 instead of the
 * `Icon` default of 1. The full list lives in the Button stories. Colors read the component alias
 * (`--cds-button-color-<property>[-<role>][-inverse][-<state>]`), then the
 * semantic action token (`--cds-color-action-*`), then the palette class they
 * replace. Token roles match the variant names: `primary`, `secondary` (and
 * `secondary-inverse` with `inverse`) and `outlined`. Unqualified names
 * (`bg-hover`, `text-disabled`, ...) are the shared defaults every
 * other style reads. A pressed color falls back to the hover color before
 * today's pressed color.
 *
 * `primaryFill`, `blackFill`, `blackOutline` and `textOnlyNeutral` are
 * deprecated aliases of `primary`, `secondary`, `outlined` and `text`
 * (`BUTTON_ROLE_VARIANTS`) and render identically.
 *
 * Tokens that only work in pairs:
 * - Disabled colors still render at `--cds-button-disabled-opacity` (0.4), so
 *   set it to `1` alongside them.
 * - A focus-ring color draws an outline on top of the default ring shadow. Set
 *   `--cds-button-focus-shadow: none` to replace the ring rather than add to it.
 *
 * `ghost` reads `--cds-button-color-text-ghost-default` (neutral 700) and
 * `--cds-button-color-bg-ghost-hover` (transparent), each over its
 * `--cds-color-action-*` token.
 *
 * `inverse` swaps `secondary` to the light `secondary-inverse` colors. On
 * `primary` it only switches the focus-ring color to
 * `focus-ring-primary-inverse`.
 *
 * `hoverTreatment: 'gradient'` layers a wash over the hover fill of
 * `primary` from `--cds-button-color-bg-primary-hover-gradient-start`
 * / `-end`, each over its `--cds-color-action-*` token; every other style
 * ignores it. The wash paints only once both stops resolve; until then the
 * solid hover color shows. It fades in over `--cds-button-transition-duration`,
 * instantly when that is unset or the user prefers reduced motion.
 */
export const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2',
    'whitespace-normal sm:whitespace-nowrap rounded-button font-body font-button',
    'transition-button duration-button ease-button',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2',
    'focus-visible:outline-button focus-visible:outline-offset-button focus-visible:outline-button-focus',
    // Without the token the legacy ring box-shadow is restated unchanged.
    'focus-visible:[box-shadow:var(--cds-button-focus-shadow,var(--tw-ring-offset-shadow),var(--tw-ring-shadow),var(--tw-shadow,0_0_#0000))]',
    // An unset token is invalid at computed-value time, so `scale` stays
    // `none`. The label and icons scale back so only the surface shrinks.
    // Reduced motion turns the shrink off even when the token is set.
    'active:[scale:var(--cds-button-scale-active)]',
    '[&:active>*]:[scale:calc(1/var(--cds-button-scale-active))]',
    'motion-reduce:active:[scale:none] motion-reduce:[&:active>*]:[scale:none]',
    'disabled:pointer-events-none disabled:opacity-button-disabled',
    'aria-busy:pointer-events-none aria-busy:cursor-progress aria-busy:opacity-button-disabled',
    'aria-disabled:pointer-events-none aria-disabled:cursor-not-allowed aria-disabled:opacity-button-disabled',
    '[&_svg]:pointer-events-none [&_svg]:size-button-icon [&_svg]:shrink-0 [&_[data-hen-icon]]:pointer-events-none [&_[data-hen-icon]]:size-button-icon [&_[data-hen-icon]]:shrink-0 dark:bg-unset dark:text-unset',
  ],
  {
    variants: {
      variant: {
        /** Primary. */
        primary: PRIMARY_STYLE,
        /** Secondary; with `inverse`, Secondary Inverse. */
        secondary: SECONDARY_STYLE,
        /** Outlined. */
        outlined: OUTLINED_STYLE,
        /** Text. */
        text: TEXT_STYLE,
        /** Ghost: transparent at rest, underlined on hover, keeps its padding. */
        ghost: GHOST_STYLE,
        /** @deprecated Use `primary`, which renders identically. */
        primaryFill: PRIMARY_STYLE,
        primaryOutline: [
          'bg-button text-primary-700',
          'border-button border-primary-700',
          'hover:bg-button-hover hover:text-button-hover',
          'active:bg-button-active active:text-button-active',
          'aria-pressed:bg-button-active',
          'disabled:bg-button-disabled disabled:text-button-brand-primary-disabled disabled:border-button-brand-primary-disabled',
          'disabled:hover:bg-button-disabled-hover disabled:hover:text-button-disabled-hover',
        ],
        /** @deprecated Use `secondary`, which renders identically. */
        blackFill: SECONDARY_STYLE,
        /** @deprecated Use `outlined`, which renders identically. */
        blackOutline: OUTLINED_STYLE,
        grayOutline: [
          'bg-button text-black',
          'border-button border-neutral-200',
          'hover:bg-button-hover',
          'active:bg-button-active',
          'aria-pressed:bg-button-active',
          'disabled:bg-button-disabled disabled:text-button-black-disabled disabled:border-button-gray-disabled',
          'disabled:hover:bg-button-disabled-hover',
        ],
        grayFill: [
          'bg-neutral-100 text-black',
          'border-2 border-transparent',
          'hover:bg-neutral-200',
          'active:bg-neutral-200',
          'aria-pressed:bg-neutral-200',
        ],
        errorOutline: [
          'bg-button text-error-500',
          'border-button border-error-500',
          'hover:bg-button-hover hover:text-error-500',
          'active:bg-button-active',
          'aria-pressed:bg-button-active',
          'disabled:bg-button-disabled disabled:text-button-error-disabled disabled:border-button-error-disabled',
          'disabled:hover:bg-button-disabled-hover',
        ],
        textOnly: [
          'bg-transparent text-primary-700 rounded-button-text-only',
          'underline-offset-button decoration-button',
          'hover:bg-transparent hover:underline hover:text-button-hover',
          'hover:decoration-button-hover hover:decoration-button-underline-hover',
          'active:bg-transparent active:text-button-active',
          NO_PRESS_SCALE,
          'aria-pressed:text-button-active',
          'disabled:text-button-brand-primary-disabled disabled:hover:text-button-disabled-hover',
          '!p-0',
        ],
        /** @deprecated Use `text`, which renders identically. */
        textOnlyNeutral: TEXT_STYLE,
        /** Brand-secondary color text; not the Secondary role. */
        textOnlySecondary: [
          'bg-transparent text-secondary-700 rounded-button-text-only',
          'underline-offset-button decoration-button',
          'hover:bg-transparent hover:underline hover:text-button-hover',
          'hover:decoration-button-hover hover:decoration-button-underline-hover',
          'active:bg-transparent active:text-button-active',
          NO_PRESS_SCALE,
          'aria-pressed:text-button-active',
          'disabled:text-button-brand-secondary-disabled disabled:hover:text-button-disabled-hover',
          '!p-0',
        ],
        textOnlyWhite: [
          'bg-transparent text-white rounded-button-text-only',
          'underline-offset-button decoration-button',
          'hover:bg-transparent hover:underline hover:text-white',
          'hover:decoration-button-hover hover:decoration-button-underline-inverse-hover',
          'active:bg-transparent active:text-white',
          NO_PRESS_SCALE,
          'aria-pressed:text-white',
          'disabled:text-button-white-disabled',
          '!p-0',
        ],
        textOnlyLink: [
          'bg-transparent text-link rounded-button-text-only',
          'font-normal text-b5 leading-b5',
          'underline underline-offset-button-link decoration-button-link',
          'hover:bg-transparent hover:underline',
          'hover:decoration-button-hover-link hover:decoration-button-underline-link',
          'active:decoration-button-hover-link active:decoration-button-underline-link-active',
          'active:bg-transparent',
          NO_PRESS_SCALE,
          'disabled:text-button-link-disabled',
          '!p-0',
        ],
        /** Icon-only, no text. Neutral color, rounded. Use with iconBefore/iconAfter and no children. */
        iconOnly: [
          'bg-transparent text-neutral-600 rounded-md',
          'hover:bg-neutral-100 hover:text-primary-700',
          'active:bg-neutral-200 active:text-primary-700',
          'aria-pressed:bg-neutral-100 aria-pressed:text-primary-700',
        ],
        iconOnlyChip: [
          'rounded-full bg-white text-primary-700 shadow-xs',
          'hover:bg-neutral-50 hover:text-primary-700',
          'active:bg-neutral-100 active:text-primary-700',
          'aria-pressed:bg-white aria-pressed:text-primary-700',
          // Saved fill in one arbitrary variant: `aria-pressed:[&_svg]` won't
          // compile and `fill-current` isn't generated here.
          '[&[aria-pressed=true]_svg]:fill-button-chip-pressed [&[aria-pressed=true]_[data-hen-icon]]:fill-button-chip-pressed',
        ],
      },
      width: buttonWidthClass,
      'width-md': buttonWidthMdClass,
      'width-lg': buttonWidthLgClass,
      size: {
        xxs: [
          'px-button-x-xxs py-button-y-xxs text-button-xxs',
          LABEL_SIZED_ICON,
        ],
        xs: ['px-button-x-xs py-button-y-xs text-button-xs', LABEL_SIZED_ICON],
        sm: 'px-button-x-sm py-button-y-sm text-button-sm',
        lg: 'px-button-x-lg py-button-y-lg text-button-lg',
        // Round single-icon button. Radius is its own token so a theme's
        // button radius cannot turn the circle into a rounded square.
        icon: 'h-button-icon-only w-button-icon-only p-button-icon-only rounded-button-icon-only gap-0 [&_svg]:size-button-icon-only-icon [&_[data-hen-icon]]:size-button-icon-only-icon',
        flush: 'p-0 h-auto',
      },
      underline: buttonUnderlineClass,
      hoverTreatment: buttonHoverTreatmentClass,
      inverse: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        // `icon` keeps its fixed icon-only height.
        variant: 'ghost',
        size: ['xxs', 'xs', 'sm', 'lg'],
        class: 'h-auto',
      },
      {
        variant: 'iconOnly',
        class:
          'h-auto min-h-0 min-w-0 p-0 px-0 py-0 gap-0 shrink-0 border-0 shadow-none',
      },
      {
        variant: 'iconOnlyChip',
        class: 'h-auto min-h-0 min-w-0 p-0 px-0 py-0 gap-0 shrink-0 border-0',
      },
      {
        variant: 'iconOnlyChip',
        size: 'icon',
        class: [
          'w-auto aspect-square rounded-full p-button-icon-only',
          'aria-pressed:hover:bg-white aria-pressed:active:bg-white',
          '[&_svg]:size-button-icon-only-icon [&_[data-hen-icon]]:size-button-icon-only-icon',
          'focus-visible:ring-focus focus-visible:ring-offset-1 focus-visible:ring-offset-focus',
          // The base `dark:` ring follows the OS color scheme; keep the chip's ring blue.
          'dark:focus-visible:ring-focus dark:focus-visible:ring-offset-focus',
        ],
      },
      {
        variant: [...SIZED_VARIANTS],
        size: 'xxs',
        class: 'h-auto min-h-button-xxs',
      },
      {
        variant: [...SIZED_VARIANTS],
        size: 'xs',
        class: 'h-auto min-h-button-xs',
      },
      {
        variant: [...SIZED_VARIANTS],
        size: 'sm',
        class: 'min-h-button-sm',
      },
      {
        variant: [...SIZED_VARIANTS],
        size: 'lg',
        class: 'min-h-button-lg',
      },
      {
        variant: [...TEXT_ONLY_VARIANTS],
        size: 'icon',
        class: 'w-auto h-auto',
      },
      {
        variant: [...UNDERLINE_AT_REST_VARIANTS],
        underline: 'always',
        class: 'underline',
      },
      {
        variant: [...PRIMARY_VARIANTS],
        inverse: true,
        class: 'focus-visible:outline-button-focus-primary-inverse',
      },
      {
        variant: [...SECONDARY_VARIANTS],
        inverse: true,
        class: [
          'bg-white text-button-secondary-inverse',
          'hover:bg-button-secondary-inverse-hover',
          'active:bg-button-secondary-inverse-active',
          'aria-pressed:bg-button-secondary-inverse-active',
          'focus-visible:outline-button-focus-inverse',
          'disabled:bg-button-secondary-inverse-disabled disabled:text-button-secondary-inverse-disabled',
          'disabled:hover:bg-button-secondary-inverse-disabled-hover',
        ],
      },
      {
        variant: [...PRIMARY_VARIANTS],
        hoverTreatment: 'gradient',
        class: [GRADIENT_LAYER, 'before:bg-button-gradient-primary'],
      },
    ],
    defaultVariants: buttonDefaultVariants,
  },
);

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;
